import { differenceInDays } from 'date-fns/differenceInDays';
import { z } from 'zod';
import { zGoDate, zGoNullableDate, zGoSlice } from '../helpers/fix-go-types';
import type { VolumeDiscount } from './product-pricing';
import { ProductPricingSchema } from './product-pricing';
import { ProductStatSchema } from './product-stat';

// form validation rules
export const ProductSchema = z.object({
  id: z.string(),
  store_id: z.string(),
  vendor_id: z.string(),

  vendor_name: z.string(),
  brand: z.string(),
  description: z.string(),
  department: z.string(),
  item_code: z.string(),
  suggested_item_code: z.string(),
  // Default barcode (QTY=0) from POS. See pkg/lpos/product.go:loadBarcodes.
  barcode: z.string(),
  barcodes: zGoSlice(
    z.object({
      barcode: z.string(),
      quantity: z.number(),
    })
  ),
  last_cost: z.number(),
  price: z.number(),
  promo_price: z.number().nullable().optional(),
  type: z.string(),
  size: z.string(),
  size_ounces: z.number(),
  case_qty: z.number(),
  standard_qty: z.number(),
  order_lot: z.string(),
  inventory_qty: z.number(),
  stats: ProductStatSchema,
  min_qoh: z.number().optional(),
  disabled: z.boolean(),
  auto_disabled: z.boolean().optional(),
  disabled_toggled_at: zGoNullableDate.optional(),
  disabled_until: zGoNullableDate.optional(),
  last_received: zGoNullableDate.optional(),
  last_sold: zGoNullableDate.optional(),
  notes: z.string(),

  // LiquorPOS's free-text BIN column, passed through verbatim — commonly
  // blank, and commonly a vendor abbreviation rather than a shelf location.
  // `.default('')` matters: products rehydrated from the persisted cache skip
  // validation entirely, and an undefined bin would break the column's sort.
  bin: z.string().default(''),
  label_ids: zGoSlice(z.number()),

  // Populated by nightly reconciliation when the product's vendor integration
  // has volume pricing; empty string otherwise. The prefix ("a_" or "u_")
  // encodes provenance — see pkg/family in the backend. useFamilies strips
  // the prefix for grouping; empty values fall into a synthetic "unassigned"
  // bucket as a defensive fallback for products not yet reconciled.
  family_id: z.string(),

  pricing: ProductPricingSchema,

  updated_at: zGoDate,
});

export type Product = z.infer<typeof ProductSchema>;

export const saleStatDays = [1, 7, 14, 30, 60, 90, 120, 150, 180, 365] as const;
export type SaleStatDays = (typeof saleStatDays)[number];

export function saleStat(p: Product, days: SaleStatDays): number {
  return p.stats[`last_${days}`];
}

const AUTO_DISABLE_DAYS = 365 as const;

// `auto_disabled` is derived on the client and nothing on the wire ever sets
// it — the backend has no such field. A product earns it by being dead on all
// three counts at once: no stock, nothing received in a year, nothing sold in
// a year. Returns a new product rather than assigning onto the argument, so a
// list can be normalized without the caller's array being mutated underneath
// it.
export function normalizeProduct(product: Product): Product {
  const noStock = product.inventory_qty <= 0;
  const notReceived =
    !product.last_received ||
    differenceInDays(new Date(), product.last_received) >= AUTO_DISABLE_DAYS;
  const notSold = saleStat(product, AUTO_DISABLE_DAYS) === 0;

  if (!(noStock && notReceived && notSold)) {
    return product;
  }

  return { ...product, auto_disabled: true };
}

export function casesSold(product: Product, period: SaleStatDays): number {
  if (product.case_qty === 0) {
    return 0;
  }

  return saleStat(product, period) / product.case_qty;
}

export function caseDiscount(product: Product) {
  if (!product.pricing.future_cost || !product.pricing.current_cost) {
    return 0;
  }

  const savings = product.pricing.future_cost - product.pricing.current_cost;
  if (savings > -0.01 && savings < 0.01) {
    return 0;
  }

  return savings;
}

export function hasCaseDiscount(product: Product) {
  return caseDiscount(product) !== 0;
}

export function volumeDiscount(product: Product, cases: number) {
  const frontline = product.pricing.current_cost ?? 0;
  if (frontline === 0) {
    return 0;
  }

  const savings = frontline - tierPrice(product, cases);
  if (savings > -0.01 && savings < 0.01) {
    return 0;
  }

  return savings;
}

export function hasVolumeDiscount(product: Product) {
  if (!product.pricing.volume_discounts || !product.pricing.current_cost) {
    return false;
  }

  return product.pricing.volume_discounts.length > 0;
}

/**
 * Whether a product has any buyup-style pricing data — at least one case
 * discount or volume discount tier. Used to decide if the product participates
 * in the buyup view.
 */
export function hasPricingData(product: Product): boolean {
  return hasCaseDiscount(product) || hasVolumeDiscount(product);
}

/**
 * Gate for the order-view → buyup-view pivot link on the product cell.
 * Volume-pricing vendors get the link whenever the product has volume
 * tiers (those deals are always active). Case-discount vendors get it
 * only when next month's case cost is strictly higher than this month's,
 * i.e. this month is the buying moment — flat or falling costs can wait.
 */
export function isPivotEligible(product: Product, vendorHasVolume: boolean): boolean {
  if (vendorHasVolume) {
    return hasVolumeDiscount(product);
  }

  const { current_cost: curr, future_cost: next } = product.pricing;

  return curr != null && next != null && curr < next;
}

/**
 * Per-case price the product actually pays at `cases` ordered — the deepest
 * volume-discount tier whose `min_cases` threshold the order reaches, falling
 * back to `current_cost` (frontline) when no tier qualifies.
 */
export function tierPrice(product: Product, cases: number): number {
  const frontline = product.pricing.current_cost ?? 0;

  return findActiveTier(product, cases)?.cost ?? frontline;
}

function findActiveTier(
  product: Product,
  cases: number
): { min_cases: number; cost: number } | null {
  const tiers = product.pricing.volume_discounts;
  if (!tiers) {
    return null;
  }

  for (let i = tiers.length - 1; i >= 0; --i) {
    if (cases >= tiers[i].min_cases) {
      return tiers[i];
    }
  }

  return null;
}

// case_qty=0 fallback avoids divide-by-zero on legacy/partial product records.
export function inventoryCases(product: Product): number {
  return product.inventory_qty / (product.case_qty || 1);
}

/**
 * Min QOH written the way the store owner set it. The value is stored in units,
 * but anything past a full case reads better as cases to someone who buys by
 * the case. Used in the Reorder column tooltips on both the order editor and
 * the product reports.
 */
export function minQOHThreshold(product: Product): string {
  const min_qoh = product.min_qoh ?? 0;

  return product.case_qty > 0 && min_qoh > product.case_qty
    ? `${min_qoh / product.case_qty} case(s)`
    : `${min_qoh} unit(s)`;
}

/**
 * A product is "active" if it's stockable (not disabled / auto-disabled) and
 * has had any unit sales in the last 90 days. Used to filter inactive members
 * out of family-level rollups (DOH min, hide-empty-family) and to gate which
 * products appear in the buyup view.
 */
export function isActiveProduct(product: Product): boolean {
  return !product.disabled && !product.auto_disabled && saleStat(product, 90) > 0;
}

export function effectivePrice(product: Product): number {
  return product.promo_price ?? product.price;
}

// Price per individual unit (effectivePrice covers standard_qty units).
export function unitPrice(product: Product): number {
  return effectivePrice(product) / product.standard_qty;
}

// Gross margin per pack (one standard_qty of units).
export function packMargin(product: Product): number {
  return effectivePrice(product) - product.standard_qty * product.last_cost;
}

// Gross margin per individual unit.
export function unitMargin(product: Product): number {
  return packMargin(product) / product.standard_qty;
}

export function hasPromoPrice(product: Product): boolean {
  return product.promo_price != null;
}

// Kneedle: normalize savings-per-case and min-cases to [0,1], return the tier with
// the greatest distance above the chord (y_norm − x_norm). Falls back to the deepest
// tier when the curve is linear (no tier stands out above the chord).
export function bestValueTierIndex(tiers: VolumeDiscount[], frontlineCost: number): number {
  if (tiers.length <= 1) {
    return 0;
  }

  const savings = tiers.map((t) => frontlineCost - t.cost);
  const cases = tiers.map((t) => t.min_cases);

  const xMin = cases[0];
  const xMax = cases[tiers.length - 1];
  const yMin = savings[0];
  const yMax = savings[tiers.length - 1];

  if (xMax === xMin || yMax === yMin) {
    return tiers.length - 1;
  }

  let bestIdx = tiers.length - 1;
  let bestDist = 0;

  for (let i = 0; i < tiers.length; i++) {
    const xn = (cases[i] - xMin) / (xMax - xMin);
    const yn = (savings[i] - yMin) / (yMax - yMin);
    const dist = yn - xn;

    if (dist > bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }

  return bestIdx;
}
